'use client';

import { useState, useEffect } from 'react';
import { useTranslations } from 'next-intl';
import Link from 'next/link';
import { useParams } from 'next/navigation';
import BaseAuctionCard from './BaseAuctionCard';
import { useFavorites } from '@/lib/hooks/useFavorites';
import { calculateTimeRemaining } from '@/lib/utils';

interface LiveAuctionCardProps {
  id: string;
  slug: string;
  title: string;
  location: string;
  startingPrice: number;
  image: string;
  endDate: string; // Status 2 (Devam Eden) için bitiş tarihi - müzayede ne zaman bitecek
  timeLeft?: {
    days: number;
    hours: number;
    minutes: number;
  };
  isFavorite?: boolean;
  onFavoriteToggle?: (id: string) => void;
  isAuthenticated?: boolean; // Server-side'dan gelecek
  priority?: boolean;
}

export default function LiveAuctionCard({
  id,
  slug,
  title,
  location,
  startingPrice,
  image,
  endDate,
  timeLeft,
  isFavorite = false,
  onFavoriteToggle,
  isAuthenticated = false,
  priority = false
}: LiveAuctionCardProps) {
  const t = useTranslations('auctions');
  const params = useParams();
  const { toggleFavorite, isLoading: favoriteLoading } = useFavorites();
  const [timeRemaining, setTimeRemaining] = useState(() => {
    // İlk hesaplama
    const calculated = calculateTimeRemaining(endDate);
    return {
      days: calculated.days,
      hours: calculated.hours,
      minutes: calculated.minutes,
      seconds: calculated.seconds,
      isExpired: calculated.isExpired
    };
  });
  const [isClient, setIsClient] = useState(false);

  useEffect(() => {
    setIsClient(true);
  }, []);

  useEffect(() => {
    if (!isClient) return;
    
    const timer = setInterval(() => {
      const calculated = calculateTimeRemaining(endDate);
      setTimeRemaining({
        days: calculated.days,
        hours: calculated.hours,
        minutes: calculated.minutes,
        seconds: calculated.seconds,
        isExpired: calculated.isExpired
      });
      
      // Müzayede süresi dolduysa timer'ı durdur
      if (calculated.isExpired) {
        clearInterval(timer);
        // Timer durduruldu, sayfa yenileme iptal edildi
      }
    }, 60000); // Her 1 dakikada güncelle

    return () => clearInterval(timer);
  }, [isClient, endDate, id]);

  const formatPrice = (price: number) => {
    if (!isClient) {
      // Server-side rendering için basit format
      return `${price.toLocaleString('tr-TR')} ₺`;
    }
    
    return new Intl.NumberFormat('tr-TR', {
      style: 'currency',
      currency: 'TRY',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(price);
  };

  const handleFavoriteClick = async () => {
    // Eğer parent'tan onFavoriteToggle geliyorsa onu kullan, yoksa kendi hook'unu kullan
    if (onFavoriteToggle) {
      onFavoriteToggle(id);
    } else {
      try {
        await toggleFavorite(id, isFavorite);
      } catch (error) {
        // Error toggling favorite
      }
    }
  };

  // Client-side rendering için güvenli zaman gösterimi
  const displayTime = isClient ? timeRemaining : (timeLeft || { days: 0, hours: 0, minutes: 0 });

  const imageOverlay = (
    <>
      {/* Live Indicator */}
      <div className="absolute top-4 left-4 w-3 h-3 bg-green-500 rounded-full shadow-lg shadow-green-500/50 animate-[blink_3s_ease-in-out_infinite]"></div>
      
      {/* Time Overlay */}
      <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 bg-black/60 backdrop-blur-sm rounded-full px-4 py-2">
        <div className="flex items-center justify-center gap-3 text-white">
          <div className="text-center">
            <div className="text-2xl font-bold leading-none tabular-nums">{displayTime.days}</div>
            <div className="text-xs uppercase">{t('timeUnits.days')}</div>
          </div>
          <div className="text-2xl font-bold">:</div>
          <div className="text-center">
            <div className="text-2xl font-bold leading-none tabular-nums">{displayTime.hours.toString().padStart(2, '0')}</div>
            <div className="text-xs uppercase">{t('timeUnits.hours')}</div>
          </div>
          <div className="text-2xl font-bold">:</div>
          <div className="text-center">
            <div className="text-2xl font-bold leading-none tabular-nums">{displayTime.minutes.toString().padStart(2, '0')}</div>
            <div className="text-xs uppercase">{t('timeUnits.minutes')}</div>
          </div>
        </div>
      </div>
    </>
  );

  const favoriteButton = isAuthenticated ? (
    <button
      onClick={handleFavoriteClick}
      disabled={favoriteLoading}
      className={`absolute top-4 right-4 w-10 h-10 bg-white/80 backdrop-blur-sm rounded-full flex items-center justify-center hover:bg-white transition-colors ${
        favoriteLoading ? 'opacity-50 cursor-not-allowed' : ''
      }`}
    >
      {favoriteLoading ? (
        <div className="w-5 h-5 border-2 border-gray-600 border-t-transparent rounded-full animate-spin"></div>
      ) : (
        <svg 
          className={`w-5 h-5 ${isFavorite ? 'text-red-500 fill-current' : 'text-gray-600'}`} 
          fill={isFavorite ? 'currentColor' : 'none'} 
          stroke="currentColor" 
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
        </svg>
      )}
    </button>
  ) : null;

  const content = (
    <div className="flex justify-between items-center mb-4">
      <span className="text-gray-600 text-sm">{t('card.startingPrice')}</span>
      <span className="text-2xl font-bold text-gray-900 tabular-nums">{formatPrice(startingPrice)}</span>
    </div>
  );

  const actionButton = (
    <div className="mt-auto">
      <Link href={`/${params.locale}/muzayedeler/${slug}`}>
        <button className="w-full py-3 px-4 rounded-lg font-bold text-white bg-red-600 hover:bg-red-700 transition-colors">
          {t('card.viewDetails')}
        </button>
      </Link>
    </div>
  );

  return (
    <BaseAuctionCard
      id={id}
      title={title}
      location={location}
      image={image}
      imageOverlay={imageOverlay}
      favoriteButton={favoriteButton}
      actionButton={actionButton}
      priority={priority}
    >
      {content}
    </BaseAuctionCard>
  );
}
